import {
    createColumns,
    createInternalLinkFormatter,
    DataTableColumn,
} from '@/components/ui/data-table-customized'
import { TFunction } from 'i18next'
import { Business } from '../../types/response'
import {
    BUSINESS_STATUS,
    BUSINESS_TYPE,
    formatDate,
    getStatusColor,
} from '../../types/utils'

/**
 * Get columns for Business List
 * Sortable fields: business_id, business_type, business_status, business_startdate, business_enddate, employee_name, department_name
 */
export const getBusinessListColumns = (
    t: TFunction
): DataTableColumn<Business>[] =>
    createColumns<Business>([
        {
            key: 'business_id',
            title: t('id'),
            width: 120,
            pinned: 'left',
            sortable: true,
            formatter: createInternalLinkFormatter({
                pathKey: 'businessAccountDetail',
                pathParams: (value) => ({ id: value.toString() }),
                label: (value) => `IBN-${value}`,
            }),
        },
        {
            key: 'employee_name',
            title: t('employee-name'),
            width: 180,
            sortable: true,
        },
        {
            key: 'department_name',
            title: t('department'),
            width: 180,
            sortable: true,
        },
        {
            key: 'business_type',
            title: t('type'),
            width: 150,
            sortable: true,
            render: (value) => {
                const type = BUSINESS_TYPE(t).find((t) => t.value === value)
                return type?.label || value
            },
        },
        {
            key: 'business_startdate',
            title: t('business-startdate'),
            width: 220,
            sortable: true,
            render: (_, row) => {
                const color = getStatusColor(row?.business_status)
                const colorClass =
                    color === 'success'
                        ? 'bg-green-100 text-green-800'
                        : color === 'error'
                          ? 'bg-red-100 text-red-800'
                          : color === 'warning'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-gray-100 text-gray-800'
                return (
                    <span
                        className={`px-2 py-0.5 rounded text-xs ${colorClass}`}
                    >
                        {formatDate(row?.business_startdate)} -{' '}
                        {formatDate(row?.business_enddate)}
                    </span>
                )
            },
        },
        {
            key: 'business_place',
            title: t('business-place'),
            width: 200,
        },
        {
            key: 'business_leader_status',
            title: t('leader'),
            width: 120,
            render: (value) => {
                const color = getStatusColor(value)
                const status = BUSINESS_STATUS(t).find((s) => s.value === value)
                const textColor =
                    color === 'success'
                        ? 'text-green-600'
                        : color === 'error'
                          ? 'text-red-600'
                          : color === 'warning'
                            ? 'text-orange-500'
                            : 'text-gray-500'
                return <span className={textColor}>{status?.label}</span>
            },
        },
        {
            key: 'business_bod_status',
            title: t('bom/bod'),
            width: 120,
            render: (value) => {
                const color = getStatusColor(value)
                const status = BUSINESS_STATUS(t).find((s) => s.value === value)
                const textColor =
                    color === 'success'
                        ? 'text-green-600'
                        : color === 'error'
                          ? 'text-red-600'
                          : color === 'warning'
                            ? 'text-orange-500'
                            : 'text-gray-500'
                return <span className={textColor}>{status?.label}</span>
            },
        },
        {
            key: 'business_status',
            title: t('status'),
            width: 130,
            sortable: true,
            render: (value) => {
                const status = BUSINESS_STATUS(t).find((s) => s.value === value)
                const color = getStatusColor(value)
                const colorClass =
                    color === 'success'
                        ? 'bg-green-100 text-green-800 border-green-300'
                        : color === 'error'
                          ? 'bg-red-100 text-red-800 border-red-300'
                          : color === 'warning'
                            ? 'bg-yellow-100 text-yellow-800 border-yellow-300'
                            : 'bg-gray-100 text-gray-800 border-gray-300'
                return (
                    <span
                        className={`px-2 py-0.5 rounded-full text-xs border ${colorClass}`}
                    >
                        {status?.label || t('unknown')}
                    </span>
                )
            },
        },
        {
            key: 'business_content',
            title: t('content'),
            width: 200,
        },
    ])
